import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Search, Trash2, Edit, ArrowLeft, Shield, Briefcase,
  CheckCircle, Calendar, CreditCard, Loader2, Eye, Ban, X, ShieldCheck, ShieldOff
} from 'lucide-react';

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user: adminUser, API, language } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [roleFilter, setRoleFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');

  // Modals
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', subscription_status: '', subscription_plan: '' });
  const [actionLoading, setActionLoading] = useState(null);

  const t = language === 'de' ? {
    title: 'Benutzerverwaltung', back: 'Zurück', searchPlaceholder: 'Nach Name, E-Mail oder Telefon suchen...',
    allRoles: 'Alle Rollen', clients: 'Kunden', professionals: 'Fachkräfte',
    allStatus: 'Alle Status', verified: 'Verifiziert', pending: 'Ausstehend', rejected: 'Abgelehnt',
    totalUsers: 'Gesamt', name: 'Name', email: 'E-Mail', phone: 'Telefon', role: 'Rolle',
    status: 'Status', subscription: 'Abonnement', actions: 'Aktionen', edit: 'Bearbeiten',
    delete: 'Löschen', deleteTitle: 'Benutzer löschen',
    deleteMessage: 'Sind Sie sicher? Diese Aktion kann nicht rückgängig gemacht werden.',
    cancel: 'Abbrechen', confirm: 'Löschen bestätigen', editTitle: 'Benutzer bearbeiten',
    save: 'Speichern', client: 'Kunde', professional: 'Fachkraft', admin: 'Admin',
    trial: 'Test', active: 'Aktiv', inactive: 'Inaktiv', blocked: 'Gesperrt',
    details: 'Details',
  } : {
    title: 'User Management', back: 'Back', searchPlaceholder: 'Search by name, email, or phone...',
    allRoles: 'All Roles', clients: 'Clients', professionals: 'Professionals',
    allStatus: 'All Status', verified: 'Verified', pending: 'Pending', rejected: 'Rejected',
    totalUsers: 'Total Users', name: 'Name', email: 'Email', phone: 'Phone', role: 'Role',
    status: 'Status', subscription: 'Subscription', actions: 'Actions', edit: 'Edit',
    delete: 'Delete', deleteTitle: 'Delete User',
    deleteMessage: 'Are you sure you want to delete this user? This action cannot be undone.',
    cancel: 'Cancel', confirm: 'Confirm Delete', editTitle: 'Edit User',
    save: 'Save Changes', client: 'Client', professional: 'Professional', admin: 'Admin',
    trial: 'Trial', active: 'Active', inactive: 'Inactive', blocked: 'Blocked',
    details: 'Details',
  };

  useEffect(() => {
    const token = localStorage.getItem('helply_token');
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => fetchUsers(), 500);
    return () => clearTimeout(debounce);
  }, [searchQuery, roleFilter, verificationFilter]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (verificationFilter !== 'all') params.append('verification_status', verificationFilter);
      const res = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEditUser = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      await axios.put(`${API}/admin/users/${selectedUser.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleQuickAction = async (action, extra = {}) => {
    if (!selectedUser) return;
    setActionLoading(action);
    try {
      const token = localStorage.getItem('helply_token');
      const res = await axios.post(
        `${API}/admin/users/${selectedUser.id}/quick-action`,
        { action, ...extra },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      // Refresh user in dialog
      const updated = await axios.get(`${API}/admin/users/${selectedUser.id}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => null);
      if (updated?.data?.user) setSelectedUser(updated.data.user);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed: ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const openDetailDialog = (user) => {
    setSelectedUser(user);
    setDetailDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name, email: user.email, phone: user.phone || '',
      subscription_status: user.subscription_status || '',
      subscription_plan: user.subscription_plan || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const getStatusBadge = (user) => {
    if (user.role === 'professional') {
      const s = user.verification_status;
      if (s === 'verified') return <Badge className="bg-green-600 text-xs">{t.verified}</Badge>;
      if (s === 'pending') return <Badge className="bg-orange-500 text-xs">{t.pending}</Badge>;
      if (s === 'rejected') return <Badge className="bg-red-600 text-xs">{t.rejected}</Badge>;
    }
    return null;
  };

  const getSubBadgeColor = (status) => {
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'trial') return 'bg-blue-100 text-blue-800';
    if (status === 'blocked') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-600';
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  const InfoRow = ({ label, value, valueClass = '' }) => (
    <div className="flex flex-col">
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</span>
      <span className={`text-sm font-medium text-gray-900 mt-0.5 ${valueClass}`}>{value || '—'}</span>
    </div>
  );

  const SectionLabel = ({ children }) => (
    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-5 mb-3 border-b border-gray-100 pb-1">{children}</p>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Users className="w-7 h-7 text-blue-600" />
            <h1 className="text-xl font-bold">{t.title}</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />{t.back}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">{t.totalUsers}: {users.length}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input placeholder={t.searchPlaceholder} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allRoles}</SelectItem>
                  <SelectItem value="client">{t.clients}</SelectItem>
                  <SelectItem value="professional">{t.professionals}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={verificationFilter} onValueChange={setVerificationFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t.allStatus}</SelectItem>
                  <SelectItem value="verified">{t.verified}</SelectItem>
                  <SelectItem value="pending">{t.pending}</SelectItem>
                  <SelectItem value="rejected">{t.rejected}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.name}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.email}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.role}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.status}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.subscription}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.actions}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {u.is_admin && <Shield className="w-3.5 h-3.5 text-purple-600" title="Admin" />}
                            {u.is_blocked && <Ban className="w-3.5 h-3.5 text-red-500" />}
                            <span className="font-medium text-sm">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {u.role === 'professional'
                              ? <Briefcase className="w-3.5 h-3.5 text-purple-600" />
                              : <Users className="w-3.5 h-3.5 text-green-600" />}
                            <span className="text-sm capitalize">{t[u.role] || u.role}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(u)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getSubBadgeColor(u.subscription_status)}`}>
                            {t[u.subscription_status] || u.subscription_status || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <button
                              title="View Details"
                              onClick={() => openDetailDialog(u)}
                              className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                              data-testid={`view-user-${u.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              title="Edit"
                              onClick={() => openEditDialog(u)}
                              className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {!u.is_admin && (
                              <button
                                title="Delete"
                                onClick={() => openDeleteDialog(u)}
                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* ===== USER DETAIL MODAL ===== */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedUser && (
            <>
              <DialogHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <DialogTitle className="text-xl font-bold">{selectedUser.name}</DialogTitle>
                    <p className="text-sm text-gray-500 mt-0.5">{selectedUser.email}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 mt-1">
                    {selectedUser.is_admin && (
                      <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded-full">Admin</span>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelectedUser(null); navigate(`/admin/users/${selectedUser.id}`); }}
                      className="text-xs border-red-200 text-red-600 hover:bg-red-50 whitespace-nowrap"
                      data-testid="view-full-profile-btn"
                    >
                      Full Profile + Docs →
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              {/* ACCOUNT */}
              <SectionLabel>Account</SectionLabel>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="User ID" value={selectedUser.id?.slice(0, 18) + '...'} />
                <InfoRow label="Role" value={<span className="capitalize">{t[selectedUser.role] || selectedUser.role}</span>} />
                <InfoRow label="Joined" value={formatDate(selectedUser.created_at)} />
                <InfoRow label="Phone" value={selectedUser.phone} />
              </div>

              {/* SUBSCRIPTION */}
              <SectionLabel>Subscription</SectionLabel>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow
                  label="Status"
                  value={
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getSubBadgeColor(selectedUser.subscription_status)}`}>
                      {t[selectedUser.subscription_status] || selectedUser.subscription_status || '—'}
                    </span>
                  }
                />
                <InfoRow label="Billing Cycle" value={selectedUser.subscription_plan || '—'} />
                <InfoRow label="Expires" value={formatDate(selectedUser.subscription_ends_at)} />
                <InfoRow label="Auto-Renew" value={selectedUser.auto_renewal ? 'ON' : 'OFF'} />
              </div>

              {/* CREDITS */}
              <SectionLabel>Credits & Usage</SectionLabel>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow
                  label="Credits"
                  value={selectedUser.credits ?? 0}
                  valueClass={selectedUser.credits > 0 ? 'text-green-600' : ''}
                />
                <InfoRow label="Promo Code Used" value={selectedUser.promo_code_used} />
              </div>

              {/* PROFILE */}
              <SectionLabel>Profile & Preferences</SectionLabel>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow label="Language" value={selectedUser.language?.toUpperCase()} />
                <InfoRow label="Canton" value={selectedUser.location?.canton || selectedUser.canton} />
                <InfoRow label="Nationality" value={selectedUser.nationality} />
                <InfoRow label="Verified" value={selectedUser.is_verified ? '✓ Yes' : 'No'} />
              </div>

              {/* ADMIN ACTIONS */}
              <SectionLabel>Admin Actions</SectionLabel>
              <div className="flex flex-wrap gap-2 mt-1">
                {/* Set Active Yearly */}
                <button
                  onClick={() => handleQuickAction('set_yearly')}
                  disabled={actionLoading !== null}
                  data-testid="action-set-yearly"
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'set_yearly' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  Set Active (Yearly)
                </button>

                {/* Set Trial */}
                <button
                  onClick={() => handleQuickAction('set_trial', { days: 30 })}
                  disabled={actionLoading !== null}
                  data-testid="action-set-trial"
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === 'set_trial' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calendar className="w-3.5 h-3.5" />}
                  Set Trial (30d)
                </button>

                {/* Block / Unblock */}
                {selectedUser.is_blocked ? (
                  <button
                    onClick={() => handleQuickAction('unblock')}
                    disabled={actionLoading !== null}
                    data-testid="action-unblock"
                    className="flex items-center gap-1.5 px-3 py-2 border border-green-400 text-green-700 hover:bg-green-50 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {actionLoading === 'unblock' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
                    Unblock User
                  </button>
                ) : (
                  !selectedUser.is_admin && (
                    <button
                      onClick={() => handleQuickAction('set_blocked')}
                      disabled={actionLoading !== null}
                      data-testid="action-block"
                      className="flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {actionLoading === 'set_blocked' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                      Block User
                    </button>
                  )
                )}

                {/* Toggle Admin */}
                {selectedUser.id !== adminUser?.id && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`${selectedUser.is_admin ? 'Revoke' : 'Grant'} admin role for ${selectedUser.name}?`)) return;
                      try {
                        const res = await axios.post(`${API}/admin/users/${selectedUser.id}/toggle-admin`);
                        toast.success(res.data.message);
                        setSelectedUser(prev => ({ ...prev, is_admin: res.data.is_admin }));
                        fetchUsers();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Failed to update admin role');
                      }
                    }}
                    data-testid="action-toggle-admin"
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors border ${
                      selectedUser.is_admin
                        ? 'border-orange-300 text-orange-600 hover:bg-orange-50'
                        : 'border-purple-300 text-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    {selectedUser.is_admin
                      ? <><ShieldOff className="w-3.5 h-3.5" /> Revoke Admin</>
                      : <><ShieldCheck className="w-3.5 h-3.5" /> Make Admin</>
                    }
                  </button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t.deleteTitle}</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600 py-2">{t.deleteMessage}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t.cancel}</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>{t.confirm}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t.editTitle}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>{t.name}</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div><Label>{t.email}</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div><Label>{t.phone}</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <div>
              <Label>{t.subscription}</Label>
              <Select value={editForm.subscription_status} onValueChange={(v) => setEditForm({ ...editForm, subscription_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">{t.trial}</SelectItem>
                  <SelectItem value="active">{t.active}</SelectItem>
                  <SelectItem value="inactive">{t.inactive}</SelectItem>
                  <SelectItem value="blocked">{t.blocked}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{language === 'en' ? 'Subscription Plan' : 'Abo-Plan'}</Label>
              <Select value={editForm.subscription_plan} onValueChange={(v) => setEditForm({ ...editForm, subscription_plan: v })}>
                <SelectTrigger><SelectValue placeholder={language === 'en' ? 'Select plan' : 'Plan wählen'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yearly">{language === 'en' ? 'Yearly' : 'Jährlich'}</SelectItem>
                  <SelectItem value="trial">{language === 'en' ? 'Trial' : 'Test'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>{t.cancel}</Button>
            <Button onClick={handleEditUser}>{t.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserManagement;
